import { useState, useRef, useEffect } from 'react';

interface RequestVehiclePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const BODY_TYPES = ['SUV', 'Truck', 'Sedan', 'Coupe', 'Hatchback', 'Minivan', 'Wagon', 'Convertible'];
const BUDGETS = ['Under $10k', '$10k–$15k', '$15k–$20k', '$20k–$25k', '$25k–$30k', '$30k+'];

export default function RequestVehiclePanel({ isOpen, onClose }: RequestVehiclePanelProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedBudget, setSelectedBudget] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fd = new FormData(form);
    fd.forEach((val, key) => data.append(key, val as string));
    if (selectedType) data.append('body_type', selectedType);
    if (selectedBudget) data.append('budget', selectedBudget);

    try {
      await fetch('https://readdy.ai/api/form/d6qqsotsbgrc47fimamg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-in panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Request a Vehicle"
        className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md flex flex-col"
        style={{
          background: 'linear-gradient(160deg, #111 0%, #1a1a1a 100%)',
          borderLeft: '1px solid rgba(245,200,66,0.2)',
          animation: 'slideInRight 0.3s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
                style={{ background: '#F5C842', color: '#000', fontFamily: "'Oswald', sans-serif" }}
              >
                CASTING CALL
              </span>
            </div>
            <h2
              className="text-xl font-black text-white"
              style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.04em' }}
            >
              Request a Vehicle
            </h2>
            <p className="text-white/50 text-xs mt-0.5">Tell us what you're looking for — we'll find it.</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            aria-label="Close panel"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {submitted ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full mb-6"
                style={{ background: 'rgba(245,200,66,0.15)', border: '2px solid #F5C842' }}
              >
                <i className="ri-check-line text-4xl text-[#F5C842]" />
              </div>
              <h3
                className="text-2xl font-black text-white mb-3"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                Request Received!
              </h3>
              <p className="text-white/60 text-sm leading-relaxed mb-8 max-w-xs">
                We're on the hunt. Our team will reach out within 24 hours with matching vehicles.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-3 rounded-lg font-bold text-sm bg-[#F5C842] text-black hover:bg-yellow-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Inventory
              </button>
            </div>
          ) : (
            <form
              id="request-vehicle-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5">
                  Your Name <span className="text-[#F5C842]">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="John Smith"
                  className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-white/30 bg-white/8 border border-white/15 focus:border-[#F5C842] focus:outline-none transition-colors"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5">
                  Email <span className="text-[#F5C842]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="you@email.com"
                  className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-white/30 border border-white/15 focus:border-[#F5C842] focus:outline-none transition-colors"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="(503) 555-0100"
                  className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-white/30 border border-white/15 focus:border-[#F5C842] focus:outline-none transition-colors"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                />
              </div>

              {/* Make / Model */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5">
                    Make
                  </label>
                  <input
                    type="text"
                    name="make"
                    placeholder="Toyota"
                    className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-white/30 border border-white/15 focus:border-[#F5C842] focus:outline-none transition-colors"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5">
                    Model
                  </label>
                  <input
                    type="text"
                    name="model"
                    placeholder="RAV4"
                    className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-white/30 border border-white/15 focus:border-[#F5C842] focus:outline-none transition-colors"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                  />
                </div>
              </div>

              {/* Body Type */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  Body Type
                </label>
                <div className="flex flex-wrap gap-2">
                  {BODY_TYPES.map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setSelectedType(prev => prev === type ? '' : type)}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                      style={{
                        background: selectedType === type ? '#F5C842' : 'rgba(255,255,255,0.08)',
                        color: selectedType === type ? '#000' : 'rgba(255,255,255,0.7)',
                        border: selectedType === type ? '1px solid #F5C842' : '1px solid rgba(255,255,255,0.15)',
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-2">
                  Budget
                </label>
                <div className="flex flex-wrap gap-2">
                  {BUDGETS.map(b => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setSelectedBudget(prev => prev === b ? '' : b)}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
                      style={{
                        background: selectedBudget === b ? '#F5C842' : 'rgba(255,255,255,0.08)',
                        color: selectedBudget === b ? '#000' : 'rgba(255,255,255,0.7)',
                        border: selectedBudget === b ? '1px solid #F5C842' : '1px solid rgba(255,255,255,0.15)',
                      }}
                    >
                      {b}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-semibold text-white/60 uppercase tracking-wider mb-1.5">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  rows={3}
                  maxLength={500}
                  placeholder="Low miles, AWD preferred, specific color, etc."
                  className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-white/30 border border-white/15 focus:border-[#F5C842] focus:outline-none transition-colors resize-none"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 rounded-xl font-black text-base tracking-wide transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                style={{
                  background: submitting ? 'rgba(245,200,66,0.5)' : '#F5C842',
                  color: '#000',
                  fontFamily: "'Oswald', sans-serif",
                  letterSpacing: '0.08em',
                }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/40 border-t-black rounded-full animate-spin" />
                    Sending…
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-search-line" />
                    FIND MY VEHICLE
                  </span>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
